import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
import ProfilePic from './profilepic/ProfilePic';
import ApiService from '../services/ApiService';

// Import icons
import leaderboardIcon from '../assets/ic_leaderboard.svg';
import collectionsIcon from '../assets/ic_collection.svg';
import categoryIcon from '../assets/ic_category.svg';
import mangaIcon from '../assets/ic_book.svg';
import homeIcon from '../assets/ic_home.svg';
import etycloud from '../assets/etycloud.svg';

const Sidebar = () => {
    const location = useLocation();
    const sessionKey = localStorage.getItem('sessionKey');
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMobileDevice, setIsMobileDevice] = useState(false);

    const [onlineFriends, setOnlineFriends] = useState([]);
    const [friendsToShow, setFriendsToShow] = useState([]);
    const [friendNowWatching, setFriendNowWatching] = useState({});

    useEffect(() => {
        // Check if mobile device
        const checkMobileDevice = () => {
            setIsMobileDevice(window.innerHeight > window.innerWidth * 1.5);
        };

        // Initial check
        checkMobileDevice();

        // Add resize listener
        window.addEventListener('resize', checkMobileDevice);

        return () => window.removeEventListener('resize', checkMobileDevice);
    }, []);

    useEffect(() => {
        const fetchAccount = async () => {
            try {
                const response = await ApiService.getSelf();
                if (response.type === "success") {
                    setAccount(response.data);
                }
            } catch (error) {
                console.error('Error fetching account:', error);
            } finally {
                setLoading(false);
            }
        };

        if (sessionKey) {
            fetchAccount();
        } else {
            setLoading(false);
        }
    }, [sessionKey]);

    // Get online friends
    const getOnlineFriends = (friendsEntities) => {
        return friendsEntities.filter(friend => {
            const lastWatchedTime = new Date(friend.lastWatchedTime);
            const currentTime = new Date();
            const differenceInMinutes = (currentTime - lastWatchedTime) / (1000 * 60);
            return differenceInMinutes <= 5;
        }).slice(0, 3);
    };

    useEffect(() => {
        if (!account || !account.friendsEntities) return;
        const online = getOnlineFriends(account.friendsEntities);
        setOnlineFriends(online);

        const base = online.length > 0
            ? online
            : [...account.friendsEntities]
                .sort((a, b) => new Date(b.lastWatchedTime) - new Date(a.lastWatchedTime))
                .slice(0, 3);
        setFriendsToShow(base);

        // Fetch latest watch snippet for each friend we plan to show
        const fetchWatching = async () => {
            try {
                const entries = await Promise.all(
                    base.map(async (f) => {
                        try {
                            const res = await ApiService.getWatchHistoryProfile(f.id, 0);
                            const list = res.data || [];
                            const first = list.length > 0 ? list[0] : null;
                            return [f.id, first];
                        } catch (e) {
                            return [f.id, null];
                        }
                    })
                );
                const map = {};
                entries.forEach(([id, snippet]) => { map[id] = snippet; });
                setFriendNowWatching(map);
            } catch (e) {
                // ignore
            }
        };
        fetchWatching();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);

    if (isMobileDevice) {
        return null;
    }

    // if (loading) {
    //     return <div className='home-menu'>
    //         <div className="loader"></div>
    //     </div>;
    // }

    const navItems = [
        { to: '/', label: 'Главная', icon: homeIcon, exact: true },
        { to: '/categories', label: 'Категории', icon: categoryIcon },
        { to: '/manga', label: 'Манга', icon: mangaIcon, beta: true },
        { to: '/leaderboard', label: 'Лидерборд', icon: leaderboardIcon },
        ...(sessionKey
            ? [{ to: `/collection/${localStorage.getItem('profileId')}`, label: 'Моя коллекция', icon: collectionsIcon, match: '/collection' }]
            : []),
    ];

    const isActive = (item) => {
        if (item.exact) {
            return location.pathname === item.to;
        }
        return location.pathname.startsWith(item.match || item.to);
    };

    return (
        <nav className='home-menu side-nav' aria-label="Разделы">
            <div className="side-nav__group">
                {navItems.map((item) => (
                    <Link
                        key={item.to}
                        to={item.to}
                        className={`side-nav__item ${isActive(item) ? 'is-active' : ''}`}
                        aria-current={isActive(item) ? 'page' : undefined}
                    >
                        <img src={item.icon} alt="" aria-hidden="true" />
                        <span>{item.label}</span>
                        {item.beta ? <span className="menu-beta">BETA</span> : null}
                    </Link>
                ))}
            </div>

            {loading ? <div className="loader"></div> :
                account != null ? (
                    <section className="side-nav__block">
                        <h3 className="side-nav__heading">Друзья в сети</h3>
                        <div className='friends-container'>
                            {friendsToShow.length > 0 ? (
                                friendsToShow.map((friend, index) => (
                                    <div key={index}>
                                        <a href={`/profile/${friend.nickname}`} className='friend'>
                                            <ProfilePic isBig={false} account={friend} />
                                            <div className='friend-details'>
                                                <p className="friend-name">{friend.nickname}</p>
                                                {(() => {
                                                    const snippet = friendNowWatching[friend.id];
                                                    const last = new Date(friend.lastWatchedTime);
                                                    const isOnline = (new Date() - last) / (1000 * 60) <= 5;
                                                    if (!snippet) {
                                                        return null;
                                                    }
                                                    const watchingNow = isOnline && !snippet.watched;
                                                    return (
                                                        <div className='friend-watching-row'>
                                                            <span className={`chip ${watchingNow ? 'chip-watch' : 'chip-last'}`}>
                                                                {watchingNow ? 'Смотрит' : 'Недавно'}
                                                            </span>
                                                            <div className='friend-watching-bottom'>
                                                                <span className='friend-watching-anime' title={snippet.animeName}>
                                                                    {snippet.animeName}
                                                                </span>
                                                                <span className='friend-watching-episode'>
                                                                    {snippet.series} серия
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </a>
                                    </div>
                                ))
                            ) : (
                                <p className="side-nav__muted">Друзей в сети пока нет</p>
                            )}
                        </div>
                    </section>
                ) : (
                    <section className="side-nav__block side-nav__cta">
                        <h3 className="side-nav__heading">Войдите в аккаунт</h3>
                        <p className="side-nav__muted">Списки, история просмотра и друзья</p>
                        <Link className="ui-btn ui-btn--light ui-btn--block" to="/login">Войти</Link>
                    </section>
                )
            }

            <div className="side-nav__links">
                <a href="/contact">Контакты</a>
                <a href="/community">Правила сообщества</a>
                <a href="/legal">Правообладателям</a>
                <a href="/privacy">Политика конфиденциальности</a>
                <a href="https://t.me/animixlib">Мы в Telegram</a>
            </div>

            <a className="side-nav__sponsor" href='https://cloud.ety.one' aria-label="Хостинг ety cloud">
                <img src={etycloud} alt="ety cloud" />
            </a>
        </nav>
    );
};

export default Sidebar;
